import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Eye, Phone, FileSpreadsheet, FileDown, MapPin, List, ChevronRight, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import {
  fetchComplaints,
  fetchComplaintsSummary,
  fetchComplaintsPoints,
  listComplaintsExportJobs,
  startComplaintsExport,
  downloadComplaintsExportJob,
  deleteComplaintsExportJob,
  startComplaintsPointsRefresh,
  listComplaintsPointsRefreshJobs,
  getComplaintsPointsRefreshJob
} from '../api';
import { getTexts } from '../utils/configLoader';
import { ExportJobsProvider, useExportJobs } from './exports/ExportJobsContext';
import ExportJobsPanel from './exports/ExportJobsPanel';
import ComplaintCaseModal from './ComplaintCaseModal';
import { toast } from '../hooks/use-toast';
import { STATUS_LABELS, STATUS_BADGE_CLASSES, STATUS_ORDER } from './complaints/status';
import { formatNegativeCategory, formatNegativeSubCategory } from '../utils/negativeLabels';

const ACTIVE_JOB_STATUSES = new Set(['queued', 'running']);
const isActivePointsJob = (job) => ACTIVE_JOB_STATUSES.has(String(job?.status ?? '').toLowerCase());

const PAGE_SIZE = 20;

const complaintsExportApi = {
  listJobs: listComplaintsExportJobs,
  startExport: startComplaintsExport,
  downloadJob: downloadComplaintsExportJob,
  deleteJob: deleteComplaintsExportJob
};

const toDateParam = (value) => (value ? format(value, 'yyyy-MM-dd') : undefined);

const formatCaseDate = (value) => {
  if (!value) return '—';
  const parts = String(value).split('-');
  if (parts.length === 3) {
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('ru-RU');
};

const ComplaintsInner = ({ dateRange }) => {
  const texts = getTexts('complaints');
  const { startExport, setIsPanelOpen } = useExportJobs();

  const [view, setView] = useState('list');
  const [summary, setSummary] = useState(null);
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [points, setPoints] = useState([]);
  const [page, setPage] = useState(1);

  const [searchPhone, setSearchPhone] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [detailFilter, setDetailFilter] = useState('all');
  const [pointFilter, setPointFilter] = useState('all');
  const [onlyUnresolved, setOnlyUnresolved] = useState(false);
  const [hideNotComplaint, setHideNotComplaint] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);

  const dateParams = useMemo(
    () => ({ date_from: toDateParam(dateRange?.from), date_to: toDateParam(dateRange?.to) }),
    [dateRange?.from, dateRange?.to]
  );

  const queryParams = useMemo(() => ({
    ...dateParams,
    search: searchPhone || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    complaint_type: typeFilter !== 'all' ? typeFilter : undefined,
    complaint_detail: detailFilter !== 'all' ? detailFilter : undefined,
    point: pointFilter !== 'all' ? pointFilter : undefined,
    only_unresolved: onlyUnresolved || undefined,
    hide_not_complaint: hideNotComplaint || undefined
  }), [dateParams, searchPhone, statusFilter, typeFilter, detailFilter, pointFilter, onlyUnresolved, hideNotComplaint]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await fetchComplaintsSummary(queryParams);
      setSummary(data);
    } catch (err) {
      console.error(err);
    }
  }, [queryParams]);

  const loadCases = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchComplaints({
        ...queryParams,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE
      });
      setCases(response.items ?? []);
      setTotal(response.total ?? 0);
    } catch (err) {
      console.error(err);
      setError('Не удалось загрузить список жалоб.');
    } finally {
      setIsLoading(false);
    }
  }, [queryParams, page]);

  const loadPoints = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchComplaintsPoints(queryParams);
      setPoints(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError('Не удалось загрузить разрез по точкам.');
    } finally {
      setIsLoading(false);
    }
  }, [queryParams]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  useEffect(() => {
    if (view === 'list') {
      loadCases();
    } else {
      loadPoints();
    }
  }, [view, loadCases, loadPoints]);

  // Сбрасываем страницу при смене фильтров.
  useEffect(() => { setPage(1); }, [searchPhone, statusFilter, typeFilter, detailFilter, pointFilter, onlyUnresolved, hideNotComplaint]);

  const typeOptions = useMemo(() => {
    const set = new Set();
    cases.forEach((c) => (c.categories ?? []).forEach((v) => v && set.add(v)));
    if (typeFilter !== 'all') set.add(typeFilter);
    return Array.from(set).sort();
  }, [cases, typeFilter]);

  const detailOptions = useMemo(() => {
    const set = new Set();
    cases.forEach((c) => (c.subCategories ?? []).forEach((v) => v && set.add(v)));
    if (detailFilter !== 'all') set.add(detailFilter);
    return Array.from(set).sort();
  }, [cases, detailFilter]);

  const handleExport = useCallback(() => {
    startExport(queryParams);
  }, [startExport, queryParams]);

  // ---- Фоновое обновление точек + поллинг прогресса ----
  const [pointsJob, setPointsJob] = useState(null);
  const pointsPollRef = useRef(null);
  const reloadRef = useRef(() => {});

  useEffect(() => {
    reloadRef.current = () => {
      loadSummary();
      if (view === 'list') loadCases(); else loadPoints();
    };
  }, [view, loadCases, loadPoints, loadSummary]);

  const pollPointsJob = useCallback((id) => {
    if (pointsPollRef.current) clearInterval(pointsPollRef.current);
    pointsPollRef.current = window.setInterval(async () => {
      try {
        const job = await getComplaintsPointsRefreshJob(id);
        setPointsJob(job);
        if (!isActivePointsJob(job)) {
          clearInterval(pointsPollRef.current);
          pointsPollRef.current = null;
          const status = String(job?.status ?? '').toLowerCase();
          if (status === 'completed') {
            toast({ title: 'Точки обновлены' });
          } else if (status === 'failed') {
            toast({ title: 'Не удалось обновить точки', description: job?.error, variant: 'destructive' });
          }
          reloadRef.current();
        }
      } catch (err) {
        console.error(err);
        clearInterval(pointsPollRef.current);
        pointsPollRef.current = null;
      }
    }, 2000);
  }, []);

  const handleRefreshPoints = useCallback(async () => {
    try {
      const response = await startComplaintsPointsRefresh(queryParams);
      const id = response?.jobId;
      if (id) {
        setPointsJob({ id, status: 'queued', processedRows: 0, totalRows: 0, progress: 0 });
        pollPointsJob(id);
      }
    } catch (err) {
      const activeId = err?.response?.data?.activeJobId;
      if (err?.response?.status === 409 && activeId) {
        toast({ title: 'Обновление точек уже выполняется' });
        pollPointsJob(activeId);
      } else {
        toast({
          title: 'Не удалось запустить обновление точек',
          description: err?.response?.data?.message ?? err.message,
          variant: 'destructive'
        });
      }
    }
  }, [queryParams, pollPointsJob]);

  // На монтировании — подхватываем активный job (переживает перезагрузку страницы).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const jobs = await listComplaintsPointsRefreshJobs();
        const active = (jobs ?? []).find(isActivePointsJob);
        if (active && !cancelled) {
          setPointsJob(active);
          pollPointsJob(active.id);
        }
      } catch {
        // нет активных задач — игнорируем
      }
    })();
    return () => {
      cancelled = true;
      if (pointsPollRef.current) {
        clearInterval(pointsPollRef.current);
        pointsPollRef.current = null;
      }
    };
  }, [pollPointsJob]);

  const pointsActive = isActivePointsJob(pointsJob);

  const openPoint = useCallback((point) => {
    setPointFilter(point);
    setView('list');
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>{texts.title}</CardTitle>
            <CardDescription>{texts.description}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border p-0.5">
              <Button
                variant={view === 'list' ? 'default' : 'ghost'}
                size="sm"
                className="gap-1"
                onClick={() => setView('list')}
              >
                <List className="h-4 w-4" /> Список
              </Button>
              <Button
                variant={view === 'points' ? 'default' : 'ghost'}
                size="sm"
                className="gap-1"
                onClick={() => setView('points')}
              >
                <MapPin className="h-4 w-4" /> По точкам
              </Button>
            </div>
            <Button variant="outline" size="sm" className="gap-1" onClick={handleRefreshPoints} disabled={pointsActive} title="Обновить точки из заказов">
              <RefreshCw className={`h-4 w-4 ${pointsActive ? 'animate-spin' : ''}`} />
              {pointsActive
                ? `Обновление точек… ${pointsJob?.processedRows ?? 0}/${pointsJob?.totalRows ?? 0}`
                : 'Обновить точки'}
            </Button>
            <Button variant="outline" size="sm" className="gap-1" onClick={handleExport}>
              <FileDown className="h-4 w-4" /> Экспорт
            </Button>
            <Button variant="outline" size="icon" onClick={() => setIsPanelOpen(true)} title="Выгрузки">
              <FileSpreadsheet className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 min-w-[220px] gap-2">
            <Input
              placeholder="Поиск по телефону"
              value={searchPhone}
              onChange={(event) => setSearchPhone(event.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[170px]"><SelectValue placeholder="Статус" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все статусы</SelectItem>
              {STATUS_ORDER.map((status) => (
                <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Тип жалобы" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все типы</SelectItem>
              {typeOptions.map((option) => (
                <SelectItem key={option} value={option}>{formatNegativeCategory(option)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={detailFilter} onValueChange={setDetailFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Детализация" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Вся детализация</SelectItem>
              {detailOptions.map((option) => (
                <SelectItem key={option} value={option}>{formatNegativeSubCategory(option)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {pointFilter !== 'all' && (
            <Badge variant="outline" className="gap-1 border-sky-200 bg-sky-50 text-sky-800">
              <MapPin className="h-3 w-3" /> {pointFilter}
              <button className="ml-1 text-sky-600" onClick={() => setPointFilter('all')}>×</button>
            </Badge>
          )}
          <div className="flex items-center gap-2">
            <Switch id="only-unresolved" checked={onlyUnresolved} onCheckedChange={setOnlyUnresolved} />
            <label htmlFor="only-unresolved" className="text-sm font-medium">Только неразобранные</label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="hide-not-complaint" checked={hideNotComplaint} onCheckedChange={setHideNotComplaint} />
            <label htmlFor="hide-not-complaint" className="text-sm font-medium">Скрыть «не жалоба»</label>
          </div>
        </CardContent>
      </Card>

      {pointsActive && (
        <div className="space-y-1 rounded-md border bg-background p-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Обновление точек — {pointsJob?.processedRows ?? 0} / {pointsJob?.totalRows ?? 0} телефонов</span>
            <span>{Math.max(0, Math.min(100, Number(pointsJob?.progress ?? 0)))}%</span>
          </div>
          <Progress value={Math.max(0, Math.min(100, Number(pointsJob?.progress ?? 0)))} />
        </div>
      )}

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard title="Уникальных жалоб" value={(summary?.uniqueComplaints ?? 0).toLocaleString()} />
        <StatCard title="Доля от обращений" value={`${summary?.complaintsSharePercent ?? 0}%`} />
        <StatCard title="Новых / неразобранных" value={(summary?.newComplaints ?? 0).toLocaleString()} />
        <StatCard title="Точка-лидер" value={summary?.topPoint ? `${summary.topPoint} (${summary.topPointCount})` : '—'} />
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
      )}

      {view === 'list' ? (
        <Card>
          <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <CardTitle>Жалобы за период</CardTitle>
            <div className="text-sm text-muted-foreground">
              Страница {page} из {totalPages} &middot; Всего {total.toLocaleString()}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Дата</TableHead>
                    <TableHead>Клиент</TableHead>
                    <TableHead>Звонков</TableHead>
                    <TableHead>Тип жалобы</TableHead>
                    <TableHead>Детализация</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-right">Подробнее</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Загрузка...</TableCell></TableRow>
                  )}
                  {!isLoading && cases.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Нет жалоб за выбранный период.</TableCell></TableRow>
                  )}
                  {cases.map((item) => (
                    <TableRow key={`${item.clientPhone}_${item.complaintDate}`}>
                      <TableCell className="whitespace-nowrap">{formatCaseDate(item.complaintDate)}</TableCell>
                      <TableCell className="font-medium">{item.clientName || item.clientPhone}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1 border-sky-200 bg-sky-50 text-sky-800">
                          <Phone className="h-3 w-3" />{item.callsCount ?? 0}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatNegativeCategory(item.type)}</TableCell>
                      <TableCell>{formatNegativeSubCategory(item.detail)}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_BADGE_CLASSES[item.status] ?? STATUS_BADGE_CLASSES.new}>
                          {STATUS_LABELS[item.status] ?? item.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => setSelectedCase({ phone: item.clientPhone, date: item.complaintDate })}>
                          <Eye className="mr-2 h-4 w-4" /> Разобрать
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Показано {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} из {total}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Назад</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Вперёд</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Жалобы по точкам</CardTitle>
            <CardDescription>Где чаще возникают клиентские проблемы. Клик по точке откроет её жалобы.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Точка</TableHead>
                    <TableHead>Уникальных жалоб</TableHead>
                    <TableHead>Новых</TableHead>
                    <TableHead>Не жалоба</TableHead>
                    <TableHead>Топ типы</TableHead>
                    <TableHead>Топ детализация</TableHead>
                    <TableHead className="text-right">Жалобы точки</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Загрузка...</TableCell></TableRow>
                  )}
                  {!isLoading && points.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Нет данных по точкам.</TableCell></TableRow>
                  )}
                  {points.map((row) => (
                    <TableRow key={row.point} className="cursor-pointer" onClick={() => openPoint(row.point)}>
                      <TableCell className="font-medium">{row.point}</TableCell>
                      <TableCell>{row.uniqueComplaints ?? 0}</TableCell>
                      <TableCell>{row.newComplaints ?? 0}</TableCell>
                      <TableCell>{row.notComplaintCount ?? 0}</TableCell>
                      <TableCell>{(row.topTypes ?? []).map(formatNegativeCategory).join(', ') || '—'}</TableCell>
                      <TableCell>{(row.topDetails ?? []).map(formatNegativeSubCategory).join(', ') || '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="gap-1" onClick={(e) => { e.stopPropagation(); openPoint(row.point); }}>
                          Открыть <ChevronRight className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <ComplaintCaseModal
        caseKey={selectedCase}
        onClose={() => setSelectedCase(null)}
        onSaved={() => { loadCases(); loadSummary(); if (view === 'points') loadPoints(); }}
      />
    </div>
  );
};

const StatCard = ({ title, value }) => (
  <Card className="p-3">
    <div className="text-xs font-medium text-muted-foreground">{title}</div>
    <div className="mt-1 text-lg font-bold">{value}</div>
  </Card>
);

const Complaints = ({ dateRange }) => (
  <ExportJobsProvider api={complaintsExportApi}>
    <ComplaintsInner dateRange={dateRange} />
    <ExportJobsPanel />
  </ExportJobsProvider>
);

export default Complaints;
